import React, { ChangeEvent, ElementRef, FC, useRef, useState } from 'react';

import { Box, Stack, debounce } from '@mui/material';

import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useIsUserRole } from '../../util/useIsUserRole';
import KvsTable from './KvsTable';
import { DialogModal } from '../../components';
import { KvsForm } from './KvsForm';
import { DEBOUNCE_TIME } from '../../configs';
import Breadcrumbs from '../../components/Breadcrumb';
import { useHandleSearch } from '../../util/useHandleSearch';
import { useLocationSearch } from '../../util/useLocationSearch';
import {
  ManagementSearch,
  ManagementToolbar,
  RefreshAction,
  ManagementPageHeader,
} from '../../components/ManagementPageLayout';

const KvsPage: FC = () => {
  metaTitle('KVS Groups');

  const searchVal = useLocationSearch();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState(searchVal || '');
  const [search, setSearch] = useState(searchVal || '');
  type KvsTableHandle = ElementRef<typeof KvsTable>;
  const kvsRef = useRef<KvsTableHandle>(null);
  const [refresh, setRefresh] = useState(false);

  const disableAddNew = useIsUserRole([
    UserRole.user,
    UserRole.layoutEditor,
    UserRole.dataEditor,
  ]);

  const onSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearch(e.target.value);
  };

  const onRefetchData = () => {
    kvsRef.current?.onRefetch();
  };

  useHandleSearch(searchText);

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'KVS',
            to: '/kvs',
            description:
              'This page stores dynamic values used in slides. Users can set values for keys and organize them into groups. Updating a value here automatically reflects changes in slides and presentations using the value. Note that values used in slides cannot be deleted.',
          },
        ]}
      />
      <ManagementPageHeader
        title="Key-value store"
        subtitle="Manage reusable dynamic values used across slides and presentations."
        actionLabel="New key group"
        onAction={() => setOpen(true)}
        actionDisabled={disableAddNew}
      />

      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search key groups"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            debounce(() => onSearch(event), DEBOUNCE_TIME)();
          }}
        />
        <Stack direction="row" spacing={1.25} justifyContent="flex-end">
          <RefreshAction onClick={() => setRefresh(!refresh)} />
        </Stack>
      </ManagementToolbar>
      <KvsTable searchText={search} ref={kvsRef} onRefresh={refresh} />
      <DialogModal
        title="New key group"
        onClose={() => setOpen(false)}
        open={open}
        width={560}
      >
        <KvsForm
          onFinished={() => setOpen(false)}
          onRefetchData={onRefetchData}
        />
      </DialogModal>
    </Box>
  );
};

export default KvsPage;
